import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import type {
  ConversationResponse as Conversation,
  TagCount,
} from "../../../api/generated";
import { TagInput } from "./TagInput";

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

interface ConversationItemProps {
  conversation: Conversation;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  onTagClick: (tag: string) => void;
  tagSuggestions: TagCount[];
  bulkMode?: boolean;
  bulkSelected?: boolean;
  onBulkToggle?: () => void;
}

export function ConversationItem({
  conversation,
  selected,
  onClick,
  onDelete,
  onRename,
  onAddTag,
  onRemoveTag,
  onTagClick,
  tagSuggestions,
  bulkMode,
  bulkSelected,
  onBulkToggle,
}: ConversationItemProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [showTagInput, setShowTagInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const tags = conversation.tags ?? [];

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(conversation.title);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    setIsEditing(false);
    if (trimmed && trimmed !== conversation.title) {
      onRename(trimmed);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <>
      <ListItemButton
        selected={selected}
        onClick={isEditing ? undefined : bulkMode ? onBulkToggle : onClick}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          py: 1.5,
          "&:hover .action-btn": { opacity: 1 },
        }}
      >
        {bulkMode && (
          <Checkbox
            checked={bulkSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={onBulkToggle}
            size="small"
            sx={{ mr: 1, p: 0 }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <TextField
              inputRef={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
              onClick={(e) => e.stopPropagation()}
              size="small"
              variant="standard"
              fullWidth
              sx={{
                "& .MuiInput-input": { fontSize: "0.875rem", fontWeight: 500 },
              }}
              slotProps={{
                htmlInput: { maxLength: 200 }
              }}
            />
          ) : (
            <Typography variant="body2" noWrap sx={{
              fontWeight: 500
            }}>
              {conversation.title}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Chip
              label={
                conversation.mode === "pre_call" ? "Pre-Call" : "Post-Call"
              }
              size="small"
              color={conversation.mode === "pre_call" ? "info" : "warning"}
              sx={{ height: 20, fontSize: "0.75rem" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                flex: 1
              }}>
              {formatRelativeTime(conversation.updated_at)}
            </Typography>
            {!isEditing && !bulkMode && (
              <>
                <IconButton
                  className="action-btn"
                  size="small"
                  onClick={handleStartEdit}
                  sx={{ opacity: 0, transition: "opacity 0.2s", p: 0.25 }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  className="action-btn"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteOpen(true);
                  }}
                  sx={{ opacity: 0, transition: "opacity 0.2s", p: 0.25 }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            )}
          </Box>

          {/* Tag chips */}
          {(tags.length > 0 || !bulkMode) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  onClick={() => onTagClick(tag)}
                  onDelete={() => onRemoveTag(tag)}
                  sx={{
                    height: 18,
                    fontSize: "0.75rem",
                    "& .MuiChip-deleteIcon": {
                      fontSize: 12,
                      opacity: 0,
                      transition: "opacity 0.2s",
                    },
                    "&:hover .MuiChip-deleteIcon": { opacity: 1 },
                  }}
                />
              ))}
              {showTagInput ? (
                <TagInput
                  suggestions={tagSuggestions}
                  existingTags={tags}
                  onAdd={(tag) => {
                    onAddTag(tag);
                    setShowTagInput(false);
                  }}
                  onClose={() => setShowTagInput(false)}
                />
              ) : (
                !bulkMode && (
                  <IconButton
                    size="small"
                    onClick={() => setShowTagInput(true)}
                    sx={{ p: 0, width: 18, height: 18 }}
                  >
                    <AddIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )
              )}
            </Box>
          )}
        </Box>
      </ListItemButton>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{conversation.title}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setDeleteOpen(false);
              onDelete();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
